import { useCallback, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  CheckCircle2,
  ExternalLink,
  EyeOff,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import { getLandingBlogPostUrl } from "@/lib/landingLinks";
import {
  deletePost,
  listAdminPosts,
  publishPost,
  unpublishPost,
  type BlogPostAdmin,
  type BlogPostStatus,
} from "@/services/blogService";

const BLOG_POSTS_QUERY_KEY = ["platform", "blog", "posts"] as const;

type StatusFilter = "all" | BlogPostStatus;

function statusVariant(
  status: BlogPostStatus,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "published":
      return "default";
    case "scheduled":
      return "secondary";
    case "draft":
      return "outline";
    case "archived":
      return "destructive";
  }
}

export function BlogListPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pagination = usePaginationFromUrl();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      page: pagination.page,
      limit: pagination.limit,
      status: statusFilter === "all" ? undefined : statusFilter,
      search: search.trim() || undefined,
    }),
    [pagination.page, pagination.limit, statusFilter, search],
  );

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: [...BLOG_POSTS_QUERY_KEY, params],
    queryFn: () => listAdminPosts(params),
  });

  const posts = data?.posts ?? [];
  const total = data?.total ?? 0;
  if (total !== pagination.total) pagination.setTotal(total);

  const publishMutation = useMutation({
    mutationFn: (id: string) => publishPost(id),
    onSuccess: () => {
      toast.success(t("blog.list.toast.published"));
      void queryClient.invalidateQueries({ queryKey: BLOG_POSTS_QUERY_KEY });
    },
    onError: () => toast.error(t("blog.list.toast.publishError")),
  });

  const unpublishMutation = useMutation({
    mutationFn: (id: string) => unpublishPost(id),
    onSuccess: () => {
      toast.success(t("blog.list.toast.unpublished"));
      void queryClient.invalidateQueries({ queryKey: BLOG_POSTS_QUERY_KEY });
    },
    onError: () => toast.error(t("blog.list.toast.unpublishError")),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePost(id),
    onSuccess: () => {
      toast.success(t("blog.list.toast.deleted"));
      setPendingDeleteId(null);
      void queryClient.invalidateQueries({ queryKey: BLOG_POSTS_QUERY_KEY });
    },
    onError: () => toast.error(t("blog.list.toast.deleteError")),
  });

  const buildActions = useCallback(
    (row: BlogPostAdmin): TableAction[] => {
      const actions: TableAction[] = [
        {
          label: t("blog.list.actions.edit"),
          icon: Pencil,
          onClick: () => navigate(`/platform/blog/${row.id}`),
        },
      ];
      if (row.status === "published") {
        actions.push({
          label: t("blog.list.actions.openInBlog"),
          icon: ExternalLink,
          onClick: () =>
            window.open(
              getLandingBlogPostUrl(row.slug),
              "_blank",
              "noopener,noreferrer",
            ),
        });
        actions.push({
          label: t("blog.list.actions.unpublish"),
          icon: EyeOff,
          onClick: () => unpublishMutation.mutate(row.id),
        });
      } else {
        actions.push({
          label: t("blog.list.actions.publish"),
          icon: CheckCircle2,
          onClick: () => publishMutation.mutate(row.id),
        });
      }
      actions.push({
        label: t("blog.list.actions.delete"),
        icon: Trash2,
        variant: "destructive",
        onClick: () => setPendingDeleteId(row.id),
      });
      return actions;
    },
    [navigate, publishMutation, t, unpublishMutation],
  );

  const pendingDeletePost = posts.find((p) => p.id === pendingDeleteId) ?? null;

  return (
    <div className="space-y-4 p-4 md:p-8">
      <PageHeader
        title={t("blog.list.title")}
        description={t("blog.list.description")}
        actions={
          <Button asChild>
            <Link to="/platform/blog/new">
              <Plus className="mr-2 h-4 w-4" />
              {t("blog.list.newPost")}
            </Link>
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as StatusFilter)}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t("blog.list.filters.status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("blog.list.filters.statusAll")}</SelectItem>
            <SelectItem value="draft">{t("blog.list.status.draft")}</SelectItem>
            <SelectItem value="scheduled">{t("blog.list.status.scheduled")}</SelectItem>
            <SelectItem value="published">{t("blog.list.status.published")}</SelectItem>
            <SelectItem value="archived">{t("blog.list.status.archived")}</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder={t("blog.list.filters.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-[280px]"
        />
      </div>

      <TableCard>
        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : posts.length === 0 ? (
          <EmptyState
            title={t("blog.list.empty.title")}
            message={t("blog.list.empty.message")}
            action={
              <Button asChild>
                <Link to="/platform/blog/new">
                  <Plus className="mr-2 h-4 w-4" />
                  {t("blog.list.newPost")}
                </Link>
              </Button>
            }
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("blog.list.columns.title")}</TableHead>
                  <TableHead>{t("blog.list.columns.status")}</TableHead>
                  <TableHead>{t("blog.list.columns.date")}</TableHead>
                  <TableHead className="w-[60px]" aria-hidden />
                </TableRow>
              </TableHeader>
              <TableBody>
                {posts.map((row) => {
                  const dateIso =
                    row.status === "scheduled" && row.scheduledFor
                      ? row.scheduledFor
                      : row.publishedAt ?? row.updatedAt;
                  return (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/platform/blog/${row.id}`)}
                    >
                      <TableCell className="font-medium">{row.title}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(row.status)}>
                          {t(`blog.list.status.${row.status}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDisplayDate(dateIso, {
                          includeTime: true,
                          locale: i18n.language,
                        })}
                      </TableCell>
                      <TableCell
                        className="w-[60px]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TableActionsMenu actions={buildActions(row)} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("blog.list.rowsPerPage")}
              pageOfLabel={t("blog.list.pageOf", {
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(pagination.page * pagination.limit, total),
                total,
              })}
              onRefresh={async () => {
                await refetch();
              }}
              isRefreshing={isFetching && !isLoading}
            />
          </>
        )}
      </TableCard>

      <ConfirmationDialog
        open={pendingDeleteId != null}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteId(null);
        }}
        title={t("blog.list.deleteDialog.title")}
        description={t("blog.list.deleteDialog.description", {
          title: pendingDeletePost?.title ?? "",
        })}
        confirmText={t("blog.list.deleteDialog.confirm")}
        cancelText={t("blog.list.deleteDialog.cancel")}
        variant="destructive"
        isLoading={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDeleteId) deleteMutation.mutate(pendingDeleteId);
        }}
      />
    </div>
  );
}
